import classnames from 'classnames'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useState, useRef, useEffect, useMemo } from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { Image } from '../image'
import { ICON_CONSTANTS, ICON_CONSTANTS_MAP } from '../productForm/icon-constants'

import * as styles from './purchase-type-selector-ds.styles'
import { mapPlansToFrequencyOptions, getPlanDetailsByPlanId } from './purchase-type-selector.helpers'
import { PURCHASE_TYPES } from './purchase-type-selector.types'

import type { PurchaseType, PurchaseTypeSelectorProps } from './purchase-type-selector.types'

// Lazy-load the drawer so its Sheet/Radio/Button (vaul + Radix) tree stays out of the PDP/QATC
// First Load JS and only downloads after hydration — mirrors the legacy FrequencySelector dynamic().
const FrequencyDrawerDS = dynamic(
  () =>
    import(/* webpackChunkName: "frequency-drawer-ds" */ './frequency-drawer-ds').then(module => ({
      default: module.FrequencyDrawerDS,
    })),
  { ssr: false },
)

export const PurchaseTypeSelectorDS = ({
  subscriptionId,
  isFromPdp,
  value,
  onChange,
  subscribeLabel,
  oneTimeLabel,
  name = 'purchase_type',
  variantSubscriptionPlans,
  selectedPlanId,
  onPlanIdChange,
  formattedSubscriptionPrice,
  subscriptionFestivalSavingPercentage,
  formattedFestivalOfferPrice,
  formattedSalePrice,
  showNewPdpDesignV2,
  product,
  pdpPriceRedesign,
}: PurchaseTypeSelectorProps) => {
  const { t } = useTranslation('common')

  const frequencyOptions = mapPlansToFrequencyOptions(variantSubscriptionPlans)

  const [isFrequencySelectorOpen, setIsFrequencySelectorOpen] = useState(false)
  const previousPlanIdRef = useRef<number | undefined>(selectedPlanId)

  const selectedPlanDetails = useMemo(
    () => getPlanDetailsByPlanId(variantSubscriptionPlans, selectedPlanId),
    [variantSubscriptionPlans, selectedPlanId],
  )

  const handleSelect = (type: PurchaseType) => {
    onChange(type)
    trackOnPurchaseTypeChange(type)
  }

  const handleFrequencyLinkClick = () => {
    previousPlanIdRef.current = selectedPlanId
    setIsFrequencySelectorOpen(true)
  }

  const handleFrequencyChange = (planId: string) => {
    onPlanIdChange(Number(planId))
  }

  const handleFrequencySave = () => {
    const existingFrequency = getPlanDetailsByPlanId(variantSubscriptionPlans, previousPlanIdRef.current)?.frequency
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_SHIPPING_FREQUENCY_CHANGE, {
      entity_type: 'user_selected',
      context: {
        existing_frequency: `${existingFrequency?.interval} ${existingFrequency?.unit}`,
        updated_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
      },
    })
    previousPlanIdRef.current = selectedPlanId
    handleFrequencyClose()
  }

  const handleFrequencyClose = () => {
    onPlanIdChange(previousPlanIdRef.current)
    setIsFrequencySelectorOpen(false)
  }

  const trackOnPurchaseTypeChange = (purchaseType: PurchaseType) => {
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_RADIO_BUTTON_SELECTED, {
      entity_type: 'user_selected',
      context: {
        subscriptions_button: purchaseType === PURCHASE_TYPES.SUBSCRIBE,
        shipping_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
        price_data: {
          regular_price: formattedSalePrice,
          subscription_price: formattedSubscriptionPrice,
          one_time_purchase_price: formattedFestivalOfferPrice ? formattedFestivalOfferPrice : formattedSalePrice,
        },
        subscription_status: {
          subscription_id: subscriptionId ?? null,
        },
        product_properties: getProductPropertiesForTracking(product),
      },
    })
  }

  const isSubscribeSelected = value === PURCHASE_TYPES.SUBSCRIBE

  const subscribeDisplayPrice = formattedSubscriptionPrice
  const subscribeStrikethroughPrice = formattedSalePrice

  const oneTimeDisplayPrice = formattedFestivalOfferPrice ? formattedFestivalOfferPrice : formattedSalePrice

  const oneTimeStrikethroughPrice = formattedFestivalOfferPrice && formattedSalePrice ? formattedSalePrice : null

  useEffect(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_RADIO_BUTTON_SELECTED, {
      // eslint-disable-next-line i18next/no-literal-string
      entity_type: 'default',
      context: {
        subscriptions_button: isSubscribeSelected,
        shipping_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        is_modal: !isFromPdp,
        price_data: {
          regular_price: formattedSalePrice,
          subscription_price: formattedSubscriptionPrice,
          one_time_purchase_price: formattedFestivalOfferPrice ? formattedFestivalOfferPrice : formattedSalePrice,
        },
        subscription_status: {
          subscription_id: subscriptionId ?? null,
        },
        product_properties: getProductPropertiesForTracking(product),
      },
    })
    trackEvent(TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_SHIPPING_FREQUENCY_CHANGE, {
      // eslint-disable-next-line i18next/no-literal-string
      entity_type: 'default',
      context: {
        existing_frequency: `${selectedPlanDetails?.frequency.interval} ${selectedPlanDetails?.frequency.unit}`,
        updated_frequency: null,
        is_modal: !isFromPdp,
        product_properties: getProductPropertiesForTracking(product),
      },
    })
  }, [])

  return (
    <div
      className={classnames(
        styles.container,
        isFromPdp && styles.adjustedPaddingForMobile,
        showNewPdpDesignV2 && styles.adjustedPaddingForMobile_noPaddingRight,
      )}
    >
      <label className={`${styles.optionCard} ${isSubscribeSelected ? styles.optionCardSelected : ''}`}>
        <div className={styles.optionHeader}>
          <input
            type="radio"
            name={name}
            value={PURCHASE_TYPES.SUBSCRIBE}
            checked={isSubscribeSelected}
            onChange={() => {
              handleSelect(PURCHASE_TYPES.SUBSCRIBE)
            }}
            className={styles.radio}
          />
          <span className={styles.optionLabel}>{subscribeLabel}</span>
          <div className={styles.priceContainer}>
            <span className={styles.strikethroughPrice}>
              <FormattedPrice amount={subscribeStrikethroughPrice} />
            </span>
            <span
              className={classnames(
                styles.currentPrice,
                pdpPriceRedesign && styles.currentPrice_pdpPriceRedesign,
                formattedFestivalOfferPrice && styles.festivalOfferPrice,
              )}
            >
              <FormattedPrice amount={subscribeDisplayPrice} />
            </span>
          </div>
        </div>

        <div className={styles.expandedDetails}>
          {subscriptionFestivalSavingPercentage ? (
            <div className={styles.benefitItem}>
              <div className={styles.iconWrapper}>
                <Image
                  url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.DISCOUNT_ICON]}
                  title={ICON_CONSTANTS.DISCOUNT_ICON}
                  alt=""
                  className={styles.iconImage}
                />
              </div>
              <span className={styles.savingsText}>
                <span className={styles.savingsTextPromo}>
                  {t('save_on_every_recurring_order_promo_text', {
                    subscriptionPromoDiscount: subscriptionFestivalSavingPercentage,
                  })}
                </span>{' '}
                <span className={styles.savingsTextPromoContinuation}>
                  {t('save_on_every_recurring_order_promo_text_continuation', {
                    subscriptionDiscount: selectedPlanDetails?.benefits?.value,
                  })}
                </span>
              </span>
            </div>
          ) : (
            <div className={styles.benefitItem}>
              <div className={styles.iconWrapper}>
                <Image
                  url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.DISCOUNT_ICON_GREEN]}
                  title={ICON_CONSTANTS.DISCOUNT_ICON_GREEN}
                  alt=""
                  className={styles.iconImage}
                />
              </div>
              <span className={styles.savingsText}>
                {t('save_on_every_recurring_order', { subscriptionDiscount: selectedPlanDetails?.benefits?.value })}
              </span>
            </div>
          )}

          <div className={styles.benefitItem}>
            <div className={styles.iconWrapper}>
              <Image
                url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.EDIT_ICON]}
                title={ICON_CONSTANTS.EDIT_ICON}
                alt=""
                className={styles.iconImage}
              />
            </div>
            <span>{t('change_or_cancel_anytime')}</span>
          </div>
          <div className={styles.benefitItem}>
            <div className={styles.iconWrapper}>
              <Image
                url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.RECURRING_ICON]}
                title={ICON_CONSTANTS.RECURRING_ICON}
                alt=""
                className={styles.iconImage}
              />
            </div>
            <span>
              {t('ships_every_label')}{' '}
              <button
                type="button"
                className={styles.frequencyLink}
                onClick={() => {
                  if (value === PURCHASE_TYPES.ONE_TIME) {
                    handleSelect(PURCHASE_TYPES.SUBSCRIBE)
                  } else {
                    handleFrequencyLinkClick()
                  }
                }}
              >
                {`${selectedPlanDetails?.frequency.displayLabel} ${selectedPlanDetails?.isDefault ? t('freq_common') : ''}`}
              </button>
            </span>
          </div>
        </div>
      </label>

      <label className={`${styles.optionCard} ${!isSubscribeSelected ? styles.optionCardSelected : ''}`}>
        <div className={styles.optionHeader}>
          <input
            type="radio"
            name={name}
            value={PURCHASE_TYPES.ONE_TIME}
            checked={!isSubscribeSelected}
            onChange={() => {
              handleSelect(PURCHASE_TYPES.ONE_TIME)
            }}
            className={styles.radio}
          />
          <span className={styles.optionLabel}>{oneTimeLabel}</span>
          <div className={styles.priceContainer}>
            {oneTimeStrikethroughPrice && (
              <span className={styles.strikethroughPrice}>
                <FormattedPrice amount={oneTimeStrikethroughPrice} />
              </span>
            )}
            <span
              className={classnames(
                styles.currentPrice,
                pdpPriceRedesign && styles.currentPrice_pdpPriceRedesign,
                formattedFestivalOfferPrice && styles.festivalOfferPrice,
              )}
            >
              <FormattedPrice amount={oneTimeDisplayPrice} />
            </span>
          </div>
        </div>
      </label>

      <FrequencyDrawerDS
        isOpen={isFrequencySelectorOpen}
        title={t('choose_frequency')}
        options={frequencyOptions}
        selectedValue={String(selectedPlanId)}
        onChange={handleFrequencyChange}
        onSave={handleFrequencySave}
        onClose={handleFrequencyClose}
        saveButtonText={t('save')}
      />
    </div>
  )
}

export default PurchaseTypeSelectorDS
